import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * Toasts, with one deliberate behaviour: an error toast can carry an action, so
 * "the worker isn't running" comes with a Retry rather than just bad news.
 */

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    ({ tone = 'info', title, message = '', action = null, duration }) => {
      const id = nextId++;
      const ttl = duration ?? (tone === 'error' ? 9000 : 4500);
      setToasts((current) => [...current, { id, tone, title, message, action }]);
      if (ttl > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), ttl),
        );
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo(
    () => ({
      toasts,
      dismiss,
      info: (title, message) => push({ tone: 'info', title, message }),
      success: (title, message) => push({ tone: 'success', title, message }),
      warn: (title, message) => push({ tone: 'warn', title, message }),
      /** @param {Error & {code?: string}} error */
      error: (error, { title, action } = {}) =>
        push({
          tone: 'error',
          title: title || errorTitle(error),
          message: error?.message || String(error),
          action,
        }),
      push,
    }),
    [toasts, dismiss, push],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

function errorTitle(error) {
  switch (error?.code) {
    case 'worker_unavailable':
      return 'Inference worker not running';
    case 'worker_busy':
      return 'GPU busy';
    case 'version_not_ready':
      return 'Version needs metadata';
    case 'validation_error':
      return 'Check the request';
    case 'network_error':
      return 'API unreachable';
    case 'capacity_exceeded':
      return 'Busy — try again shortly';
    case 'model_starting':
      return 'Model still loading';
    case 'model_not_loaded':
      return 'Model not loaded';
    case 'model_capacity_reached':
      return 'No room for another model';
    case 'model_server_unavailable':
      return 'Model server not reachable';
    case 'serving_not_prepared':
      return 'Version not prepared for serving';
    default:
      return 'Something went wrong';
  }
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}
